/**
 * Display formatting, ported from the legacy `Misc.number_format` /
 * `formatNumberN` helpers (src/app/data/api-client/misc.ts): PHP-style
 * `number_format(value, decimals, '.', ',')`, then trailing zeros (and a
 * dangling '.') stripped — `19.50` shows as `19.5`, `19.00` as `19`.
 *
 * DISPLAY ONLY. Nothing in this app does arithmetic on money: amounts the
 * backend sends are rendered, amounts the user types are sent on as the
 * exact string typed (legacy-migration RULES.md rule 3). The backend's own
 * pre-formatted strings (`amountFormatted`) are rendered verbatim.
 */
export function numberFormat(value: number | string | null | undefined, decimals: number): string {
  const n = typeof value === 'string' ? Number(value) : value ?? 0
  if (!Number.isFinite(n)) return String(value ?? '')
  const fixed = Math.abs(n).toFixed(decimals)
  const [intPart, frac] = fixed.split('.')
  const grouped = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const sign = n < 0 && Number(fixed) !== 0 ? '-' : ''
  return frac ? `${sign}${grouped}.${frac}` : `${sign}${grouped}`
}

export function formatNumberN(value: number | string | null | undefined, decimals = 2): string {
  const s = numberFormat(value, decimals)
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}

/** Decimals the legacy credit views used per currency: HUF 0,
 * credit/USD/EUR/GBP 2, anything else (crypto-like, e.g. USDC) 8. */
export function creditDecimals(currency: string | null | undefined): number {
  const c = String(currency ?? '').toLowerCase()
  if (c === 'huf') return 0
  if (c === 'credit' || c === 'usd' || c === 'eur' || c === 'gbp') return 2
  return 8
}

export function formatCredit(value: number | string | null | undefined, currency: string | null | undefined): string {
  return formatNumberN(value, creditDecimals(currency))
}

/** `USD 19.99` — the legacy price label (currency upper-cased in front). */
export function formatPrice(price: number | string | null | undefined, currency: string | null | undefined): string {
  return `${String(currency ?? '').toUpperCase()} ${formatNumberN(price, 2)}`.trim()
}

/** Legacy `<app-datetime-display>` renders UTC by default
 * (`toUTCString()`); here `YYYY-MM-DD HH:MM UTC`, or '' when missing (never
 * "Invalid Date"). Accepts W3C strings, unix seconds, and the PHP DateTime
 * array (`{date: "2026-09-27 19:50:53.009874", timezone: "Europe/Berlin"}`)
 * the admin startonline route answers (contract §3, b5.admin.startonline) —
 * that one is shown as its wall-clock time plus zone name. */
export function formatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'object') {
    const v = value as { date?: unknown; timezone?: unknown }
    if (typeof v.date === 'string') return `${v.date.slice(0, 16)} ${typeof v.timezone === 'string' ? v.timezone : ''}`.trim()
    return ''
  }
  const d = typeof value === 'number' ? new Date(value * (value < 1e12 ? 1000 : 1)) : new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
}

export function formatDate(value: unknown): string {
  return formatDateTime(value).slice(0, 10)
}

/** Minor units → major, as a string, without float division
 * (`1999` → `19.99`). The one place legacy divided by 100. */
export function minorToMajor(total: number): string {
  const neg = total < 0
  const abs = String(Math.abs(Math.trunc(total))).padStart(3, '0')
  return `${neg ? '-' : ''}${numberFormat(abs.slice(0, -2), 0)}.${abs.slice(-2)}`
}
